import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { 
  Person, 
  CalendarToday, 
  AccessTime,
  PlayCircleFilled,
  Star,
  BookmarkBorder,
  Bookmark,
  Share,
  MenuBook,
  VideoLibrary,
  ExpandLess,
  Info,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Close as CloseIcon,
  School as SchoolIcon
} from "@mui/icons-material";
import { 
  Typography, 
  Button, 
  IconButton, 
  Card, 
  CardContent,
  CardActions,
  CardMedia,
  Divider,
  Avatar,
  LinearProgress,
  Fab,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Snackbar
} from "@mui/material";
import 'react-quill/dist/quill.snow.css';
import Comments from "./Comments";
import Learn from "./Learn";
import Description from "./Description";
import CourseAccordion from "./CourseAccordion";  
import CourseIncludes from "./CourseIncludes";
import coursesService from "../../services/CoursesService";
import analyticsService from "../../services/AnalyticsService";
import teacherService from "../../services/TeacherService";
import useNotifications from "../../hooks/useNotifications";
import { useUsuarioConCedula } from "../../hooks/useUsuarioConCedula";
import { useWelcomeCourseStatus } from "../../hooks/useWelcomeCourseStatus";

export default function Icourse() {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: courseIdFromParams } = useParams();
    const { objeto: courseIdFromState } = location.state || {};
    const [curso, setCurso] = useState({});
    const [teacher, setTeacher] = useState({});
    const [videoSrc, setVideoSrc] = useState(null);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [activeTab, setActiveTab] = useState('description');
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [actualCourseProgress, setActualCourseProgress] = useState(0);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [enrollmentChecked, setEnrollmentChecked] = useState(false);
    const [hasRequestedUsuario, setHasRequestedUsuario] = useState(false);
    const [openObjectiveDialog, setOpenObjectiveDialog] = useState(false);
    const [openRelatedCoursesDialog, setOpenRelatedCoursesDialog] = useState(false);
    const [relatedCourses, setRelatedCourses] = useState([]);
    const [relatedCoursesLoading, setRelatedCoursesLoading] = useState(false);
    const [relatedCoursesTeachers, setRelatedCoursesTeachers] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const [videoError, setVideoError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [shouldAutoplay, setShouldAutoplay] = useState(true);
    const [accordionRefreshKey, setAccordionRefreshKey] = useState(0);
    const [shareSnackbar, setShareSnackbar] = useState({ open: false, message: "", severity: "success" });
    const videoRef = useRef(null);
    const videoContainerRef = useRef(null);
    const courseVideoFocusRef = useRef(null);
    const relatedCarouselRef = useRef(null);
    const isVideoLoadingRef = useRef(true);

    const { 
        usuarioConCedula: usuarioConCedulaContext, 
        isLoadingUsuario, 
        refreshUsuarioConCedula 
    } = useUsuarioConCedula();

    const {
        isBlocked: welcomeBlocked,
        courseId: welcomeCourseId,
        courseTitle: welcomeCourseTitle,
        isWelcomeCourse,
        refresh: refreshWelcomeStatus,
    } = useWelcomeCourseStatus();

    const usuarioConCedula = useMemo(() => {
        if (usuarioConCedulaContext && typeof usuarioConCedulaContext === "object") {
            return usuarioConCedulaContext;
        }
        try {
            const stored = localStorage.getItem("usuarioConCedula");
            return stored ? JSON.parse(stored) : {};
        } catch (parseError) {
            console.error("Error al leer usuarioConCedula desde localStorage:", parseError);
            return {};
        }
    }, [usuarioConCedulaContext]);

    const courseId = courseIdFromParams || courseIdFromState;

    const handleScrollRelatedCourses = useCallback((direction) => {
        const container = relatedCarouselRef.current;
        if (!container) return;
        const cardWidth = 280;
        const gap = 16;
        const scrollAmount = cardWidth + gap;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    }, []);
    
    const { refetch: refetchNotifications } = useNotifications(
      `/api/Notification/${usuarioConCedula?.id}`,
      `${process.env.REACT_APP_LOCATION}/notificationHub`,
      `${process.env.REACT_APP_LOCATION}`
    );

    useEffect(() => {
        const checkMobile = () => {
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                                   window.innerWidth < 768;
            setIsMobile(isMobileDevice);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (
            !hasRequestedUsuario &&
            !isLoadingUsuario &&
            (!usuarioConCedula?.id || !usuarioConCedula?.cedula)
        ) {
            refreshUsuarioConCedula?.();
            setHasRequestedUsuario(true);
        }
    }, [
        hasRequestedUsuario,
        isLoadingUsuario,
        usuarioConCedula?.id,
        usuarioConCedula?.cedula,
        refreshUsuarioConCedula
    ]);

    const extractTextFromHtml = useCallback((htmlString) => {
        if (!htmlString) return "Sin título";
        try {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlString;
            return tempDiv.textContent || tempDiv.innerText || "Sin título";
        } catch (error) {
            return htmlString.replace(/<[^>]*>/g, '').trim() || "Sin título";
        }
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "Fecha no disponible";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    }, []);

    const checkEnrollment = useCallback(async () => {
        
        if (!courseId) {
            setError("ID de curso no válido");
            setEnrollmentChecked(true);
            return;
        }

        if (!usuarioConCedula?.id) {
            setError("Usuario no encontrado. Por favor, inicia sesión nuevamente.");
            setEnrollmentChecked(true);
            return;
        }

        try {
            // Gate de bienvenida: no permitir abrir otros cursos hasta completar el welcome
            let welcomeStatus = null;
            try {
                welcomeStatus = await refreshWelcomeStatus();
            } catch {
                welcomeStatus = null;
            }
            const blocked =
                welcomeStatus?.hasWelcomeCourse &&
                !welcomeStatus?.canAccessOtherCourses;
            if (blocked && welcomeStatus?.courseId && String(courseId) !== String(welcomeStatus.courseId)) {
                setIsEnrolled(false);
                setError(
                    `Debes completar el curso de bienvenida${
                      welcomeStatus.courseTitle ? ` «${welcomeStatus.courseTitle}»` : ''
                    } antes de acceder a otros cursos.`
                );
                setEnrollmentChecked(true);
                return;
            }

            const userCourses = await coursesService.getUserCoursesWithCedula(usuarioConCedula, navigate);
            
            if (!userCourses || !Array.isArray(userCourses)) {
                setError("Error al obtener información de cursos del usuario.");
                setEnrollmentChecked(true);
                return;
            }

            const courseIdStr = String(courseId);
            const courseIdNum = parseInt(courseId, 10);
            
            const enrolled = userCourses.some(course => {
                const courseIdFromAPI = course.id;
                const isMatch = courseIdFromAPI == courseIdStr || 
                               courseIdFromAPI == courseIdNum || 
                               String(courseIdFromAPI) === courseIdStr ||
                               parseInt(courseIdFromAPI, 10) === courseIdNum;
                
                return isMatch;
            });
            
            setIsEnrolled(enrolled);
            
            if (!enrolled) {
                setError("No tienes acceso a este curso. Debes estar inscrito para ver el contenido.");
            } else {
                setError(null);
            }
        } catch (error) {
            setError("Error al verificar el acceso al curso. Por favor, intenta de nuevo.");
        } finally {
            setEnrollmentChecked(true);
        }
    }, [courseId, usuarioConCedula, navigate, refreshWelcomeStatus]);

    const fetchCourse = useCallback(async () => {
        if (!courseId) {
            setError("No se proporcionó un ID de curso válido");
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const data = await coursesService.getCourseById(courseId);
            setCurso(data);
            
            if (data.videos && data.videos.length > 0) {
                // Buscar el primer video que no esté visto
                const firstUnwatchedIndex = data.videos.findIndex(video => video.isWatched !== true);
                
                if (firstUnwatchedIndex !== -1) {
                    // Si hay un video no visto, abrir ese con autoplay
                    setVideoSrc(data.videos[firstUnwatchedIndex].path);
                    setCurrentVideoIndex(firstUnwatchedIndex);
                    setShouldAutoplay(true);
                } else {
                    // Si todos están vistos, abrir el primero sin autoplay
                    setVideoSrc(data.videos[0].path);
                    setCurrentVideoIndex(0);
                    setShouldAutoplay(false);
                }
            }
            
            setError(null);
        } catch (error) {
            console.error("Error fetching course:", error);
            setError("Error al cargar el curso. Por favor, intenta de nuevo.");
        } finally {
            setIsLoading(false);
            setIsVideoLoading(false);
        }
    }, [courseId]);

    /** Recarga solo los datos del curso (videos con isWatched) sin cambiar el video actual. Útil para actualizar el acordeón. */
    const refreshCourseData = useCallback(async () => {
        if (!courseId) return;
        try {
            const data = await coursesService.getCourseById(courseId);
            setCurso(prev => ({ ...prev, ...data }));
        } catch (err) {
            console.error('Error refreshing course data:', err);
        }
    }, [courseId]);

    const fetchTeacher = useCallback(async (teacherId) => {
        if (!teacherId) return;
        
        try {
            const teacherData = await teacherService.getTeacher(teacherId);
            setTeacher(teacherData);
        } catch (error) {
            console.error("Error fetching teacher:", error);
        }
    }, []);

    const fetchCourseProgress = useCallback(async () => {
        if (!courseId || !usuarioConCedula?.id) return;
        
        try {
            const result = await coursesService.getCourseProgress(usuarioConCedula.id, courseId);
            const raw = Number(result.data.progressPercentage);
            const actualProgress = isNaN(raw) ? 0 : Math.floor(raw);
            setActualCourseProgress(actualProgress);
            
            try {
                await refetchNotifications();
            } catch (notificationError) {
                console.error('Error refreshing notifications:', notificationError);
            }
        } catch (error) {
            console.error('Error getting course progress:', error);
            setActualCourseProgress(0);
        }
    }, [courseId, usuarioConCedula?.id, refetchNotifications]);

    useEffect(() => {
        checkEnrollment();
    }, [checkEnrollment]);

    useEffect(() => {
        if (enrollmentChecked && isEnrolled) {
            fetchCourse();
            fetchCourseProgress();
        }
    }, [enrollmentChecked, isEnrolled, fetchCourse, fetchCourseProgress]);

    // Al completar el curso de bienvenida, refrescar el gate para desbloquear el resto
    useEffect(() => {
        if (actualCourseProgress >= 100 && isWelcomeCourse(courseId)) {
            refreshWelcomeStatus();
        }
    }, [actualCourseProgress, courseId, isWelcomeCourse, refreshWelcomeStatus]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!enrollmentChecked || isLoading) {
                setError("La carga del curso está tomando más tiempo de lo esperado. Por favor, intenta de nuevo.");
                setIsLoading(false);
                setEnrollmentChecked(true);
            }
        }, 30000);

        return () => clearTimeout(timeoutId);
    }, [enrollmentChecked, isLoading]);

    useEffect(() => {
        if (curso.teacherId) {
            fetchTeacher(curso.teacherId);
        }
    }, [curso.teacherId, fetchTeacher]);

    const handleVideoSelect = useCallback((path, index) => {
        if (!path) return;
        
        setVideoSrc(path);
        setCurrentVideoIndex(index);
        isVideoLoadingRef.current = true;
        setIsVideoLoading(true);
        setProgress(0);
        setVideoError(null);
        setRetryCount(0);
        
        // Verificar si el video está visto para controlar autoplay
        const selectedVideo = curso.videos?.[index];
        if (selectedVideo && selectedVideo.isWatched === true) {
            setShouldAutoplay(false);
        } else {
            setShouldAutoplay(true);
        }
        
        if (window.innerWidth < 1024) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [curso.videos]);

    const handleVideoEnded = useCallback(async () => {
        const userId = usuarioConCedula?.id;
        const courseId = curso.id;

        const videoId = curso.videos?.[currentVideoIndex]?.id;
        const videoEl = videoRef.current;
        
        try {
            if (userId && courseId && videoId) {
                if (videoEl?.duration) {
                    analyticsService.trackVideoProgress({
                        userId,
                        videoId,
                        courseId,
                        positionSeconds: videoEl.duration,
                        durationSeconds: videoEl.duration,
                        eventType: 'complete',
                        throttleMs: 0,
                    });
                }

                await coursesService.markVideoAsWatched(userId, courseId, videoId);

                setCurso(prevCurso => {
                    if (!prevCurso?.videos) return prevCurso;
                    const updatedVideos = prevCurso.videos.map(video => {
                        if (video.id === videoId && video.isWatched !== true) {
                            return { ...video, isWatched: true };
                        }
                        return video;
                    });
                    return { ...prevCurso, videos: updatedVideos };
                });
                setAccordionRefreshKey(prev => prev + 1);
                await refreshCourseData();
                await fetchCourseProgress();

                const nextIndex = currentVideoIndex + 1;
                if (curso.videos && nextIndex < curso.videos.length) {
                    handleVideoSelect(curso.videos[nextIndex].path, nextIndex);
                }
            } else {
                console.warn('Missing required parameters for marking video as watched:', {
                    userId: !!userId,
                    courseId: !!courseId,
                    videoId: !!videoId
                });
            }
        } catch (error) {
            console.error("Error marking video as watched:", error);
        }
    }, [usuarioConCedula?.id, curso.id, curso.videos, currentVideoIndex, handleVideoSelect, fetchCourseProgress, refreshCourseData]);

    const toggleBookmark = useCallback(() => {
        setIsBookmarked(prev => !prev);
    }, []);

    const handleVideoProgress = useCallback((e) => {
        const video = e.target;
        if (video.duration > 0) {
            const progressPercent = (video.currentTime / video.duration) * 100;
            setProgress(progressPercent);

            const videoId = curso.videos?.[currentVideoIndex]?.id;
            if (videoId) {
                analyticsService.trackVideoProgress({
                    userId: usuarioConCedula?.id,
                    videoId,
                    courseId: curso.id,
                    positionSeconds: video.currentTime,
                    durationSeconds: video.duration,
                    eventType: 'heartbeat',
                });
            }
        }
    }, [curso.videos, curso.id, currentVideoIndex, usuarioConCedula?.id]);

    const handleVideoLoadStart = useCallback(() => {
        isVideoLoadingRef.current = true;
        setIsVideoLoading(true);
        setVideoError(null);
    }, []);

    const handleVideoLoadedMetadata = useCallback(() => {
        if (isMobile) {
            isVideoLoadingRef.current = false;
            setIsVideoLoading(false);
            setVideoError(null);
        }
    }, [isMobile]);

    const handleVideoLoadedData = useCallback(() => {
        isVideoLoadingRef.current = false;
        setIsVideoLoading(false);
        setVideoError(null);
        setRetryCount(0);
        if (videoRef.current && !isMobile && shouldAutoplay) {
            videoRef.current.play().catch(error => {
                console.log('Autoplay bloqueado por el navegador:', error);
            });
        }
    }, [isMobile, shouldAutoplay]);

    const handleVideoError = useCallback((e) => {
        isVideoLoadingRef.current = false;
        setIsVideoLoading(false);
        const video = e.target;
        const errorCode = video.error;
        let errorMessage = "Error al cargar el video.";
        
        if (errorCode) {
            switch (errorCode.code) {
                case MediaError.MEDIA_ERR_ABORTED:
                    errorMessage = "La carga del video fue cancelada.";
                    break;
                case MediaError.MEDIA_ERR_NETWORK:
                    errorMessage = isMobile 
                        ? "Error de conexión. Verifica tu conexión a internet y vuelve a intentar."
                        : "Error de red al cargar el video. Verifica tu conexión.";
                    break;
                case MediaError.MEDIA_ERR_DECODE:
                    errorMessage = "Error al decodificar el video. El formato puede no ser compatible.";
                    break;
                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = "El formato de video no es compatible con tu dispositivo.";
                    break;
                default:
                    errorMessage = isMobile
                        ? "No se pudo cargar el video. Verifica tu conexión e intenta de nuevo."
                        : "Error desconocido al cargar el video.";
            }
        }
        
        setVideoError(errorMessage);
        console.error("Error de video:", errorCode, errorMessage);
    }, [isMobile]);

    const retryVideoLoad = useCallback(() => {
        if (videoRef.current && videoSrc && retryCount < 3) {
            setRetryCount(prev => prev + 1);
            setVideoError(null);
            isVideoLoadingRef.current = true;
            setIsVideoLoading(true);
            
            const video = videoRef.current;
            const currentSrc = video.src;
            video.src = '';
            setTimeout(() => {
                video.src = currentSrc;
                video.load();
            }, 500);
        }
    }, [videoSrc, retryCount]);

    useEffect(() => {
        if (!videoRef.current || !videoSrc) {
            return;
        }

        const video = videoRef.current;
        const timeoutDuration = isMobile ? 60000 : 20000;

        const loadingTimeout = setTimeout(() => {
            if (!isVideoLoadingRef.current) {
                return;
            }

            if (isMobile) {
                isVideoLoadingRef.current = false;
                setIsVideoLoading(false);
                return;
            }

            isVideoLoadingRef.current = false;
            setIsVideoLoading(false);
            if (retryCount < 3) {
                setVideoError("El video está tardando en cargar. Reintentando...");
                retryVideoLoad();
            } else {
                setVideoError("Timeout al cargar el video. Por favor, recarga la página.");
            }
            console.warn('Timeout al cargar el video');
        }, timeoutDuration);

        const tryPlay = () => {
            if (video.readyState >= 2 && !isMobile && shouldAutoplay) {
                video.play().catch(error => {
                    console.log('Autoplay bloqueado por el navegador:', error);
                });
            }
        };

        if (video.readyState >= 2 && !isMobile && shouldAutoplay) {
            tryPlay();
        } else if (!isMobile && shouldAutoplay) {
            video.addEventListener('loadeddata', tryPlay, { once: true });
        }

        const handleCanPlay = () => {
            isVideoLoadingRef.current = false;
            setIsVideoLoading(false);
            setVideoError(null);
        };

        const handleWaiting = () => {
            if (isMobile) {
                isVideoLoadingRef.current = true;
                setIsVideoLoading(true);
            }
        };

        const handlePlaying = () => {
            isVideoLoadingRef.current = false;
            setIsVideoLoading(false);
            setVideoError(null);
        };

        const handleStalled = () => {
            if (isMobile) {
                isVideoLoadingRef.current = true;
                setIsVideoLoading(true);
            }
        };

        video.addEventListener('loadedmetadata', handleVideoLoadedMetadata);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('stalled', handleStalled);

        return () => {
            clearTimeout(loadingTimeout);
            video.removeEventListener('loadeddata', tryPlay);
            video.removeEventListener('loadedmetadata', handleVideoLoadedMetadata);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('playing', handlePlaying);
            video.removeEventListener('stalled', handleStalled);
        };
    }, [videoSrc, currentVideoIndex, isMobile, retryCount, retryVideoLoad, shouldAutoplay, handleVideoLoadedMetadata]);

    useEffect(() => {
        const video = videoRef.current;
        const container = videoContainerRef.current;

        if (video) {
            const preventContextMenu = (e) => {
                e.preventDefault();
                return false;
            };

            const preventDrag = (e) => {
                e.preventDefault();
                return false;
            };

            const preventKeyboardShortcuts = (e) => {
                if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
                    e.preventDefault();
                    return false;
                }
                if ((e.ctrlKey || e.metaKey) && (e.shiftKey && (e.key === 'I' || e.key === 'i'))) {
                    e.preventDefault();
                    return false;
                }
                if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
                    e.preventDefault();
                    return false;
                }
                if ((e.ctrlKey || e.metaKey) && (e.shiftKey && (e.key === 'C' || e.key === 'c'))) {
                    e.preventDefault();
                    return false;
                }
                if (e.key === 'F12') {
                    e.preventDefault();
                    return false;
                }
                if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'J' || e.key === 'j')) {
                    e.preventDefault();
                    return false;
                }
            };

            const preventSelection = (e) => {
                if (e.target === video || e.target === container) {
                    e.preventDefault();
                    return false;
                }
            };

            const preventCopy = (e) => {
                if (e.target === video || e.target === container) {
                    e.preventDefault();
                    return false;
                }
            };

            video.addEventListener('contextmenu', preventContextMenu);
            video.addEventListener('dragstart', preventDrag);
            video.addEventListener('selectstart', preventSelection);
            video.addEventListener('copy', preventCopy);
            container?.addEventListener('contextmenu', preventContextMenu);
            container?.addEventListener('dragstart', preventDrag);
            container?.addEventListener('selectstart', preventSelection);
            container?.addEventListener('copy', preventCopy);
            document.addEventListener('keydown', preventKeyboardShortcuts);

            return () => {
                video.removeEventListener('contextmenu', preventContextMenu);
                video.removeEventListener('dragstart', preventDrag);
                video.removeEventListener('selectstart', preventSelection);
                video.removeEventListener('copy', preventCopy);
                container?.removeEventListener('contextmenu', preventContextMenu);
                container?.removeEventListener('dragstart', preventDrag);
                container?.removeEventListener('selectstart', preventSelection);
                container?.removeEventListener('copy', preventCopy);
                document.removeEventListener('keydown', preventKeyboardShortcuts);
            };
        }
    }, [videoSrc, currentVideoIndex]);

    const buildShareUrl = useCallback(() => {
        const courseTitle = extractTextFromHtml(curso.title);
        return `${window.location.origin}/?shared=${curso.id}&title=${encodeURIComponent(courseTitle)}`;
    }, [curso.id, curso.title, extractTextFromHtml]);

    const copyShareLink = useCallback(async (shareUrl) => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setShareSnackbar({
                open: true,
                message: "Enlace del curso copiado al portapapeles",
                severity: "success",
            });
        } catch {
            setShareSnackbar({
                open: true,
                message: "No se pudo copiar el enlace. Inténtalo de nuevo.",
                severity: "error",
            });
        }
    }, []);

    const handleShare = useCallback(async () => {
        if (!curso?.id) {
            setShareSnackbar({
                open: true,
                message: "El curso aún no está disponible para compartir.",
                severity: "warning",
            });
            return;
        }

        const courseTitle = extractTextFromHtml(curso.title);
        const shareUrl = buildShareUrl();
        const text = `¡Mira este curso: ${courseTitle}!`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: courseTitle,
                    text,
                    url: shareUrl,
                });
                setShareSnackbar({
                    open: true,
                    message: "Curso compartido correctamente",
                    severity: "success",
                });
            } catch (error) {
                if (error?.name === "AbortError") return;
                await copyShareLink(shareUrl);
            }
        } else {
            await copyShareLink(shareUrl);
        }
    }, [buildShareUrl, copyShareLink, curso?.id, curso.title, extractTextFromHtml]);

    const fetchRelatedCourses = useCallback(async () => {
        if (!usuarioConCedula?.id) return;
        
        setRelatedCoursesLoading(true);
        try {
            const response = await coursesService.getSuggestedCourses(usuarioConCedula.id, navigate, 1, 6);
            const courses = response.courses || [];
            
            // Filtrar el curso actual
            const filteredCourses = courses.filter(c => c.id !== curso.id).slice(0, 3);
            setRelatedCourses(filteredCourses);
            
            // Obtener nombres de instructores
            const uniqueTeacherIds = [...new Set(filteredCourses.map(c => c.teacherId || c.teacher_id))].filter(id => id > 0);
            const namesMap = {};
            
            await Promise.all(uniqueTeacherIds.map(async (id) => {
                try {
                    const teacherData = await teacherService.getTeacher(id);
                    namesMap[id] = teacherData.name || teacherData.nombre || 'Instructor no disponible';
                } catch (error) {
                    console.error(`Error al obtener instructor con ID ${id}:`, error);
                    namesMap[id] = 'Instructor no disponible';
                }
            }));
            
            setRelatedCoursesTeachers(namesMap);
        } catch (error) {
            console.error('Error al obtener cursos relacionados:', error);
            setRelatedCourses([]);
        } finally {
            setRelatedCoursesLoading(false);
        }
    }, [usuarioConCedula?.id, navigate, curso.id]);

    const handleOpenRelatedCourses = useCallback(() => {
        setOpenRelatedCoursesDialog(true);
        if (relatedCourses.length === 0) {
            fetchRelatedCourses();
        }
    }, [relatedCourses.length, fetchRelatedCourses]);

    const handleEnrollRelatedCourse = useCallback(async (relatedCourseId) => {
        if (!usuarioConCedula?.id) return;

        if (welcomeBlocked && !isWelcomeCourse(relatedCourseId)) {
            alert('Debes completar el curso de bienvenida antes de solicitar otros cursos.');
            return;
        }
        
        try {
            await coursesService.assignUserCourse(usuarioConCedula.id, relatedCourseId, navigate);
            alert('Solicitud de inscripción enviada con éxito');
        } catch (error) {
            console.error('Error al inscribirse al curso:', error);
            const apiMessage =
                error?.response?.data?.message ||
                (typeof error?.response?.data === 'string' ? error.response.data : null) ||
                'Error al inscribirse al curso. Inténtalo de nuevo.';
            alert(apiMessage);
        }
    }, [usuarioConCedula?.id, navigate, welcomeBlocked, isWelcomeCourse]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const prev = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";
        return () => {
            window.history.scrollRestoration = prev;
        };
    }, []);

    useLayoutEffect(() => {
        if (isLoading || !enrollmentChecked || !isEnrolled || error || !curso?.id) return;
        const el = courseVideoFocusRef.current;
        if (!el) return;
        const id = requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: "auto", block: "start" });
            try {
                el.focus({ preventScroll: true });
            } catch (_) {
                /* noop */
            }
        });
        return () => cancelAnimationFrame(id);
    }, [isLoading, enrollmentChecked, isEnrolled, error, curso?.id]);

    if (isLoading || !enrollmentChecked) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
                <div className="text-center">
                    <CircularProgress size={50} sx={{ color: '#4DABA4', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {!enrollmentChecked ? 'Verificando acceso...' : 'Cargando curso...'}
                    </Typography>
                </div>
            </div>
        );
    }

    if (error || (enrollmentChecked && !isEnrolled)) {
        const isWelcomeGateError =
            welcomeBlocked &&
            welcomeCourseId &&
            String(courseId) !== String(welcomeCourseId);

        return (
            <div className="flex justify-center items-center p-4 min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
                <Alert 
                    severity={!isEnrolled ? "warning" : "error"} 
                    className="max-w-md"
                    action={
                        <div className="flex gap-2">
                            {isWelcomeGateError ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() =>
                                        navigate(`/courses/Introcourse/${welcomeCourseId}`, {
                                            state: { objeto: welcomeCourseId },
                                        })
                                    }
                                >
                                    Ir a bienvenida
                                </Button>
                            ) : (
                                !isEnrolled && (
                                    <Button 
                                        color="inherit" 
                                        size="small"
                                        onClick={() => navigate('/courses/getCourses')}
                                    >
                                        Ver Cursos
                                    </Button>
                                )
                            )}
                            <Button 
                                color="inherit" 
                                size="small"
                                onClick={() => window.location.reload()}
                            >
                                Reintentar
                            </Button>
                        </div>
                    }
                >
                    <Typography variant="h6" className="mb-2">
                        {isWelcomeGateError
                            ? 'Curso de bienvenida pendiente'
                            : !isEnrolled
                              ? 'Acceso Denegado'
                              : 'Error'}
                    </Typography>
                    {error || 'No tienes acceso a este curso. Debes estar inscrito para ver el contenido.'}
                    {isWelcomeGateError && welcomeCourseTitle ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Curso requerido: {welcomeCourseTitle}
                        </Typography>
                    ) : null}
                </Alert>
            </div>
        );
    }

    const courseTitle = extractTextFromHtml(curso.title);
    const totalVideos = curso.videos?.length || 0;
    const completedVideos = Math.round((actualCourseProgress / 100) * totalVideos) || 0;
    const courseProgress = actualCourseProgress;
    const currentVideo = curso.videos?.[currentVideoIndex];

    return (
        <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
            <div className="sticky top-0 z-40 border-b shadow-sm backdrop-blur-sm lg:hidden bg-white/90">
                <div className="px-4 py-3">
                    <div className="flex justify-between items-center">
                        <h1 className="flex-1 mr-4 text-lg font-bold text-gray-800 truncate">
                            {courseTitle}
                        </h1>
                        <div className="flex items-center space-x-2">
                            <IconButton size="small" onClick={handleShare}>
                                <Share />
                            </IconButton>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-[1400px] mx-auto px-4 py-6">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
                    <div className="flex flex-col flex-1 gap-6 min-w-0">
                        <div
                            ref={courseVideoFocusRef}
                            tabIndex={-1}
                            className="relative outline-none scroll-mt-16 lg:scroll-mt-8"
                        >
                            {videoSrc ? (
                                <Card className="overflow-hidden bg-gradient-to-br from-gray-900 to-black shadow-2xl">
                                    <div 
                                        ref={videoContainerRef}
                                        className="relative w-full pb-[56.25%] bg-gradient-to-br from-gray-900 to-black"
                                        style={{
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none',
                                            MozUserSelect: 'none',
                                            msUserSelect: 'none'
                                        }}
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                    >
                                        {isVideoLoading && (
                                            <div className="flex absolute inset-0 z-10 justify-center items-center bg-gray-900">
                                                <div className="text-center">
                                                    <div className="mx-auto mb-2 w-12 h-12 rounded-full border-b-2 border-blue-500 animate-spin"></div>
                                                    {isMobile && (
                                                        <p className="text-sm text-white">Cargando video...</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {videoError && (
                                            <div className="flex absolute inset-0 z-10 justify-center items-center bg-gray-900 bg-opacity-95">
                                                <div className="px-4 max-w-md text-center">
                                                    <Alert 
                                                        severity="error" 
                                                        className="mb-4"
                                                        action={
                                                            retryCount < 3 ? (
                                                                <Button 
                                                                    size="small" 
                                                                    onClick={retryVideoLoad}
                                                                    sx={{ color: 'inherit' }}
                                                                >
                                                                    Reintentar
                                                                </Button>
                                                            ) : null
                                                        }
                                                    >
                                                        <Typography variant="body2" className="text-sm">
                                                            {videoError}
                                                        </Typography>
                                                    </Alert>
                                                    {isMobile && (
                                                        <Typography variant="caption" className="block mt-2 text-xs text-gray-400">
                                                            Si el problema persiste, verifica tu conexión a internet o intenta recargar la página.
                                                        </Typography>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        
                                        <video
                                            ref={videoRef}
                                            key={`${videoSrc}-${currentVideoIndex}-${retryCount}`}
                                            className="object-cover absolute top-0 left-0 w-full h-full"
                                            controls
                                            controlsList="nodownload noremoteplayback"
                                            disablePictureInPicture
                                            playsInline
                                            onEnded={handleVideoEnded}
                                            onTimeUpdate={handleVideoProgress}
                                            onPause={() => {
                                                const video = videoRef.current;
                                                const videoId = curso.videos?.[currentVideoIndex]?.id;
                                                if (!video || !videoId || video.ended || video.duration <= 0) return;
                                                analyticsService.trackVideoProgress({
                                                    userId: usuarioConCedula?.id,
                                                    videoId,
                                                    courseId: curso.id,
                                                    positionSeconds: video.currentTime,
                                                    durationSeconds: video.duration,
                                                    eventType: 'abandon',
                                                    throttleMs: 0,
                                                });
                                            }}
                                            onLoadStart={handleVideoLoadStart}
                                            onLoadedMetadata={handleVideoLoadedMetadata}
                                            onLoadedData={handleVideoLoadedData}
                                            onError={handleVideoError}
                                            onContextMenu={(e) => e.preventDefault()}
                                            onDragStart={(e) => e.preventDefault()}
                                            style={{ 
                                                backgroundColor: "#111827",
                                                userSelect: 'none',
                                                WebkitUserSelect: 'none',
                                                MozUserSelect: 'none',
                                                msUserSelect: 'none',
                                                pointerEvents: 'auto'
                                            }}
                                            preload={isMobile ? "metadata" : "auto"}
                                        >
                                            <source src={`${process.env.REACT_APP_LOCATION}${videoSrc}`} type="video/mp4" />
                                            Tu navegador no soporta el elemento de video.
                                        </video>

                                        <div className="absolute inset-0 bg-gradient-to-t via-transparent pointer-events-none from-black/20 to-black/10" />
                                    </div>
                                </Card>
                            ) : (
                                <Card className="overflow-hidden shadow-2xl">
                                    <div className="relative w-full pb-[56.25%] bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                        <div className="flex absolute inset-0 justify-center items-center">
                                            <div className="text-center text-white">
                                                <VideoLibrary className="mb-4 text-6xl opacity-50" />
                                                <Typography variant="h6" className="opacity-75">
                                                    {totalVideos > 0 ? 'Selecciona un video para comenzar' : 'No hay videos disponibles'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </div>

                        <div className="lg:hidden">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <Card className="flex-1 min-w-0 bg-white border-0 shadow-md rounded-xl">
                                    <CardContent className="p-3 sm:p-3.5">
                                        <div className="flex justify-between items-center gap-2 mb-1.5">
                                            <Typography variant="body2" className="text-xs font-bold text-gray-800 sm:text-sm">
                                                Progreso del Curso
                                            </Typography>
                                            <div className="flex justify-center items-center min-w-[1.75rem] h-7 px-1.5 text-[11px] font-bold text-white rounded-full bg-[#4DABA4] sm:text-xs">
                                                {Math.round(courseProgress)}%
                                            </div>
                                        </div>
                                        <LinearProgress
                                            variant="determinate"
                                            value={courseProgress}
                                            sx={{
                                                height: 7,
                                                borderRadius: 4,
                                                mb: 1,
                                                bgcolor: 'rgba(77, 171, 164, 0.15)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    bgcolor: '#4DABA4',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" className="text-xs">
                                            {completedVideos} de {totalVideos} videos completados
                                        </Typography>
                                    </CardContent>
                                </Card>
                                {curso.objective && (
                                    <Tooltip title="Guia del curso">
                                        <IconButton
                                            size="medium"
                                            onClick={() => setOpenObjectiveDialog(true)}
                                            sx={{
                                                backgroundColor: '#4DABA4',
                                                color: 'white',
                                                flexShrink: 0,
                                                '&:hover': { backgroundColor: '#3d8a84' },
                                            }}
                                        >
                                            <Info />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Cursos relacionados">
                                    <IconButton
                                        size="medium"
                                        onClick={handleOpenRelatedCourses}
                                        sx={{
                                            backgroundColor: '#4DABA4',
                                            color: 'white',
                                            flexShrink: 0,
                                            '&:hover': { backgroundColor: '#3d8a84' },
                                        }}
                                    >
                                        <Lightbulb />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                    <h1 className="mb-2 text-2xl font-bold text-gray-900 lg:text-3xl">
                                        {courseTitle}
                                    </h1>
                                    <div className="flex items-center space-x-4 text-gray-600">
                                        <div className="flex items-center space-x-1">
                                            <Person className="text-sm" />
                                            <span className="text-sm">
                                                Instructor: {teacher.name || curso.teacherId || 'Por asignar'}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <CalendarToday className="text-sm" />
                                            <span className="text-sm">{formatDate(curso.createdAt)}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <VideoLibrary className="text-sm" />
                                            <span className="text-sm">{totalVideos} videos</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Tooltip title="Compartir">
                                        <IconButton size="large" onClick={handleShare}>
                                            <Share />
                                        </IconButton>
                                    </Tooltip>

                                </div>
                            </div>
                        </div>

                        <div className="lg:hidden">
                            <div className="flex p-1 space-x-1 bg-white rounded-lg shadow-sm">
                                {[
                                    { id: 'description', label: 'Descripción', icon: MenuBook },
                                    { id: 'learn', label: 'Aprenderás', icon: Star },
                                    { id: 'comments', label: 'Comentarios', icon: Person }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex-1 flex items-center justify-center space-x-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-blue-500 text-white shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <tab.icon className="text-xs" />
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-6">
                            <div className="hidden lg:flex lg:flex-col lg:gap-6">
                                <Description desc={curso.description} />
                                <Learn topics={curso.labels || []} />
                                <Comments 
                                    videoId={currentVideo?.id} 
                                    courseId={curso.id || courseId} 
                                />
                            </div>

                            <div className="lg:hidden">
                                {activeTab === 'description' && <Description desc={curso.description} />}
                                {activeTab === 'learn' && <Learn topics={curso.labels || []} />}
                                {activeTab === 'comments' && (
                                    <Comments 
                                        videoId={currentVideo?.id} 
                                        courseId={curso.id || courseId} 
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 w-full lg:w-1/3 shrink-0">
                        <div className="hidden lg:flex items-center gap-2 sm:gap-3">
                            <Card className="flex-1 min-w-0 bg-white border-0 shadow-md rounded-xl">
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex justify-between items-center gap-2 mb-2">
                                        <Typography variant="body2" className="text-sm font-bold text-gray-800">
                                            Progreso del Curso
                                        </Typography>
                                        <div className="flex justify-center items-center min-w-[2rem] h-8 px-2 text-xs font-bold text-white rounded-full bg-[#4DABA4]">
                                            {Math.round(courseProgress)}%
                                        </div>
                                    </div>
                                    <LinearProgress
                                        variant="determinate"
                                        value={courseProgress}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            mb: 1,
                                            bgcolor: 'rgba(77, 171, 164, 0.15)',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 4,
                                                bgcolor: '#4DABA4',
                                            },
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" className="text-xs">
                                        {completedVideos} de {totalVideos} videos completados
                                    </Typography>
                                </CardContent>
                            </Card>
                            {curso.objective && (
                                <Tooltip title="Guia del curso">
                                    <IconButton
                                        size="medium"
                                        onClick={() => setOpenObjectiveDialog(true)}
                                        sx={{
                                            backgroundColor: '#4DABA4',
                                            color: 'white',
                                            flexShrink: 0,
                                            '&:hover': { backgroundColor: '#3d8a84' },
                                        }}
                                    >
                                        <Info />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Cursos relacionados">
                                <IconButton
                                    size="medium"
                                    onClick={handleOpenRelatedCourses}
                                    sx={{
                                        backgroundColor: '#4DABA4',
                                        color: 'white',
                                        flexShrink: 0,
                                        '&:hover': { backgroundColor: '#3d8a84' },
                                    }}
                                >
                                    <Lightbulb />
                                </IconButton>
                            </Tooltip>
                        </div>

                        <Typography variant="h6" className="font-bold text-gray-800">
                            Contenido del Curso
                        </Typography>
                        <Card className="border-0 shadow-lg">
                            <CardContent className="p-6">
                                <CourseAccordion 
                                    key={`course-accordion-${accordionRefreshKey}-desktop`}
                                    videos={curso.videos || []} 
                                    documents={curso.documents || []} 
                                    questionnaires={curso.questionnaires || []} 
                                    onSelectVideo={handleVideoSelect}
                                    currentVideoIndex={currentVideoIndex}
                                    courseProgress={courseProgress}
                                    userName={usuarioConCedula?.name || ''}
                                    userCedula={usuarioConCedula?.cedula || ''}
                                    courseTitle={courseTitle}
                                    onQuizClick={(quizId, quizTitle) => {
                                        navigate(`/courses/millionaire-game/${quizId}`, {
                                            state: { courseId: courseId }
                                        });
                                    }}
                                />
                            </CardContent>
                        </Card>

                        {Object.keys(curso).length > 0 && (
                            <CourseIncludes data={curso} />
                        )}
                    </div>
                </div>
            </main>

            <div className="fixed right-6 bottom-6 z-50 lg:hidden">
                <Fab 
                    color="primary" 
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    sx={{
                        background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #3B82A1 0%, #2563EB 100%)',
                        }
                    }}
                >
                    <MenuBook />
                </Fab>
            </div>

            {showMobileMenu && (
                <div className="fixed inset-0 z-50 lg:hidden bg-black/50" onClick={() => setShowMobileMenu(false)}>
                    <div 
                        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 border-b">
                            <div className="flex justify-between items-center">
                                <Typography variant="h6" className="font-bold">
                                    Contenido del Curso
                                </Typography>
                                <IconButton onClick={() => setShowMobileMenu(false)}>
                                    <ExpandLess />
                                </IconButton>
                            </div>
                        </div>
                        <div className="p-4">
                            <CourseAccordion 
                                key={`course-accordion-${accordionRefreshKey}-mobile`}
                                videos={curso.videos || []} 
                                documents={curso.documents || []} 
                                questionnaires={curso.questionnaires || []} 
                                onSelectVideo={(path, index) => {
                                    handleVideoSelect(path, index);
                                    setShowMobileMenu(false);
                                }}
                                currentVideoIndex={currentVideoIndex}
                                courseProgress={courseProgress}
                                userName={usuarioConCedula?.name || ''}
                                userCedula={usuarioConCedula?.cedula || ''}
                                courseTitle={courseTitle}
                                onQuizClick={(quizId, quizTitle) => {
                                    navigate(`/courses/millionaire-game/${quizId}`, {
                                        state: { courseId: courseId }
                                    });
                                    setShowMobileMenu(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <Dialog 
                open={openObjectiveDialog} 
                onClose={() => setOpenObjectiveDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 'bold'
                }}>
                    <Info />
                    Guia del curso
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {curso.objective ? (
                        <Typography
                            component="div"
                            variant="body1"
                            className="ql-editor"
                            sx={{ 
                                lineHeight: 1.7, 
                                color: '#2d3748',
                                p: '0 !important',
                                overflow: 'visible',
                                '& ul, & ol': { pl: 3, m: 0 },
                                '& p': { m: 0, mb: 1 },
                            }}
                            dangerouslySetInnerHTML={{ __html: curso.objective }}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ 
                            lineHeight: 1.7, 
                            color: '#2d3748',
                            whiteSpace: 'pre-wrap'
                        }}>
                            No hay objetivo definido para este curso.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setOpenObjectiveDialog(false)}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #3d8a84 0%, #2563EB 100%)',
                            }
                        }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openRelatedCoursesDialog} 
                onClose={() => setOpenRelatedCoursesDialog(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontWeight: 'bold',
                    pb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Lightbulb />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Cursos relacionados
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={() => setOpenRelatedCoursesDialog(false)}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2, p: 3 }}>
                    {relatedCoursesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: '#4DABA4' }} />
                        </Box>
                    ) : relatedCourses.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <SchoolIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                No hay cursos relacionados disponibles
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => handleScrollRelatedCourses('left')}
                                sx={{
                                    position: 'absolute',
                                    left: -16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'white',
                                    boxShadow: 2,
                                    zIndex: 2,
                                    '&:hover': { bgcolor: 'grey.100' }
                                }}
                            >
                                <ChevronLeft />
                            </IconButton>
                            <Box
                                ref={relatedCarouselRef}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    overflowX: 'auto',
                                    scrollSnapType: 'x mandatory',
                                    scrollBehavior: 'smooth',
                                    pb: 1,
                                    px: 0.5,
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}
                            >
                                {relatedCourses.map((course) => {
                                    const stripHtmlTags = (html) => {
                                        if (!html) return "";
                                        const doc = new DOMParser().parseFromString(html, 'text/html');
                                        return doc.body.textContent || "";
                                    };
                                    
                                    return (
                                        <Box
                                            key={course.id}
                                            sx={{
                                                scrollSnapAlign: 'start',
                                                flex: '0 0 auto',
                                                width: 280,
                                                minWidth: 280,
                                                maxWidth: 280,
                                                display: 'flex'
                                            }}
                                        >
                                            <Card sx={{ 
                                                width: '100%',
                                                height: 420,
                                                minHeight: 420,
                                                maxHeight: 420,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 2,
                                                boxShadow: 2,
                                                transition: 'all 0.3s',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: 4
                                                }
                                            }}>
                                                <Box sx={{ position: 'relative', width: '100%', height: 160, minHeight: 160, flexShrink: 0, overflow: 'hidden' }}>
                                                    <CardMedia
                                                        component="img"
                                                        image={course.thumbnailUrl ? `${process.env.REACT_APP_LOCATION}${course.thumbnailUrl}` : '/img/default.png'}
                                                        alt={stripHtmlTags(course.title)}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            left: 8,
                                                            bgcolor: '#4DABA4',
                                                            borderRadius: 1.5,
                                                            px: 1,
                                                            py: 0.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5
                                                        }}
                                                    >
                                                        <img 
                                                            src="/icons/Logo completo.png" 
                                                            alt="Docentia" 
                                                            style={{ height: '14px', objectFit: 'contain' }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', fontSize: '0.65rem' }}>
                                                            Docentia
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <CardContent sx={{ flex: '1 1 auto', p: 2, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            mb: 1,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            fontSize: '1rem',
                                                            lineHeight: 1.3
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: course.title }}
                                                    />
                                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {relatedCoursesTeachers[course.teacherId || course.teacher_id] || 'Instructor no disponible'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {new Date(course.createdAt).toLocaleDateString('es-ES', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            3 horas
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                                <CardActions sx={{ p: 2, pt: 0, flexShrink: 0 }}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => handleEnrollRelatedCourse(course.id)}
                                                        sx={{
                                                            backgroundColor: '#4DABA4',
                                                            color: 'white',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderRadius: 1,
                                                            py: 1,
                                                            '&:hover': {
                                                                backgroundColor: '#3d8a84',
                                                            }
                                                        }}
                                                    >
                                                        Inscríbete al curso
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <IconButton
                                onClick={() => handleScrollRelatedCourses('right')}
                                sx={{
                                    position: 'absolute',
                                    right: -16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'white',
                                    boxShadow: 2,
                                    zIndex: 2,
                                    '&:hover': { bgcolor: 'grey.100' }
                                }}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button
                        onClick={() => setOpenRelatedCoursesDialog(false)}
                        variant="outlined"
                        sx={{
                            borderColor: '#4DABA4',
                            color: '#4DABA4',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            py: 1,
                            '&:hover': {
                                borderColor: '#4DABA4',
                                backgroundColor: 'rgba(77, 171, 164, 0.1)'
                            }
                        }}
                    >
                        Ahora no
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={shareSnackbar.open}
                autoHideDuration={4000}
                onClose={() => setShareSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setShareSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={shareSnackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {shareSnackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}